<template>
    <div class="u-full-height u-flex u-flex-col">
        <div class="row mx-0 mb-24 u-flex--vertical-end">
            <div class="col-12 col-md px-0 mb-18 mb-0@md">
                <h2 class="u-text-normal mb-12">
                    Archiv <span class="text-light">({{ documents.length }})</span>
                </h2>
                <app-button icon="plus" size="small" class="mr-6" @click.native="showAddModal = true">Hochladen</app-button>
                <app-button
                    icon="three-bars"
                    size="small"
                    theme="highlight"
                    @click.native="showFilterModal = true"
                >Filter</app-button>
            </div>
            <div v-if="documents.length" class="col-12 col-md">
                <div class="sort">
                    <div class="row u-flex--vertical-align">
                        <label for="sortBy" class="u-bold col mb-0 p-0 pr-12@md">Sortierung:</label>
                        <div class="col-8 p-0">
                            <select v-model="sortBy" id="sortBy" class="form-control" style="height: 40px;">
                                <option value="updated_desc">Letzte Änderung (absteigend)</option>
                                <option value="date_asc">Ausstelldatum (aufsteigend)</option>
                                <option value="date_desc">Ausstelldatum (absteigend)</option>
                                <option value="name_asc">Titel (A-Z)</option>
                                <option value="name_desc">Titel (Z-A)</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <loading-overlay :active="isLoading">
            <div v-if="documents.length" class="u-flex u-flex-wrap pb-24">
                <template v-for="(document, i) in documents">
                    <separator
                        v-if="showSeparator(i)"
                        :key="`separator-${document.id}`"
                        class="col-12"
                    >{{ getSeparatorLabel(i) }}</separator>
                    <document-thumb
                        :key="`doc-${document.id}`"
                        v-bind="document"
                        @delete="onDeleteDocument"
                        @reload="loadDocuments"
                    />
                </template>

                <div class="col-12">
                    <pagination
                        v-if="numPages > 1"
                        class="mt-24"
                        :page="page"
                        :max-items-per-page="maxItemsPerPage"
                        :num-documents="numDocuments"
                        :num-pages="numPages"
                        @update="onPaginationUpdate"
                    />
                </div>
            </div>
            <div v-else-if="!isLoading" class="u-flex u-flex--center u-flex-grow pt-56">
                <div class="u-text-center">
                    <app-icon name="file" :scale="4" hidden class="mb-12" />
                    <p class="text-mute">{{ emptyText }}</p>
                </div>
            </div>
        </loading-overlay>

        <add-document-modal
            v-if="showAddModal"
            v-model="showAddModal"
            document-status="published"
            @add="onAddDocument"
        />

        <document-filter-modal
            v-if="showFilterModal"
            v-model="showFilterModal"
            :filters="filters"
            @submit="onFilterSubmit"
        />
    </div>
</template>

<script>
import { assoc, equals } from 'ramda'
import AppIcon from '@/components/ui/Icon.vue'
import Button from '@/components/ui/Button.vue'
import DocumentThumb from '@/components/DocumentThumb.vue'
import AddDocumentModal from '@/components/ui/modals/AddDocumentModal.vue'
import Separator from '@/components/Separator.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import DocumentFilterModal from '@/components/ui/modals/DocumentFilterModal.vue'
import Pagination from '@/components/Pagination.vue'

export default {
    name: 'Documents',

    components: {
        Pagination,
        DocumentFilterModal,
        LoadingOverlay,
        Separator,
        DocumentThumb,
        AppIcon,
        AppButton: Button,
        AddDocumentModal,
    },

    data () {
        return {
            search: '',

            numDocuments: 0, // Number of all documents
            documents: [], // Paginated documents

            showAddModal: false,
            showFilterModal: false,
            sortBy: 'date_desc',
            isLoading: true,

            filters: this.createForm(),

            numPages: 0,
            page: 1,
            maxItemsPerPage: 5,
        }
    },

    computed: {
        emptyText () {
            if (this.hasFilters) {
                return 'Es wurden keine Dokumente mit deinen Kriterien gefunden.'
            }

            return 'Du hast noch keine Dokumente hochgeladen.'
        },

        hasFilters () {
            return !!(this.filters.searchTerm || this.filters.month || this.filters.year || this.filters.source);
        },
    },

    watch: {
        $route: 'updateSearch',
        sortBy: 'loadDocuments',
    },

    mounted () {
        this.updateSearch()
        this.loadDocuments()
    },

    methods: {
        async loadDocuments () {
            this.isLoading = true

            try {
                const response = await this.$http.get(`/documents?${this.getFilterQuery()}`)

                this.numPages = response.data?.numPages || this.numPages
                this.numDocuments = response.data?.numDocuments || 0
                this.documents = response.data?.documents || []
                this.maxItemsPerPage = response.data?.maxItemsPerPage || this.maxItemsPerPage
            } catch (e) {
                console.log(e)
                this.$toast.open({ type: 'error', message: e.message })
            } finally {
                setTimeout(() => {
                    this.isLoading = false
                }, 1000)
            }
        },

        createForm () {
            return {
                searchTerm: this.$route.query?.searchTerm || '',
                month: this.$route.query?.month || '',
                year: this.$route.query?.year || '',
                source: this.$route.query?.source || '',
                // @TODO tags: this.$route.query?.tags || [],
            }
        },

        getFilterQuery () {
            let filterData = this.filters || {}
            filterData = assoc('sortBy', this.sortBy, filterData)
            filterData = assoc('page', this.page, filterData)

            return new URLSearchParams(filterData).toString()
        },

        updateSearch () {
            this.search = this.$route.query?.searchTerm || ''
            this.filters.searchTerm = this.search
            this.loadDocuments()
        },

        onPaginationUpdate (newPage) {
            this.page = newPage
            this.loadDocuments()
        },

        onDeleteDocument (documentId) {
            const index = this.documents.findIndex(x => x.id === documentId)
            this.documents.splice(index, 1)
        },

        onAddDocument (document) {
            this.showAddModal = false
            this.documents.unshift(document)
        },

        showSeparator (index) {
            if (this.sortBy.startsWith('updated')) return false
            if (index === 0) return true

            if (this.sortBy.startsWith('date')) {
                const currentYear = this.$date(new Date(this.documents[index].date), 'y')
                const previousYear = this.$date(new Date(this.documents[index - 1].date), 'y')

                return currentYear !== previousYear
            }

            if (this.sortBy.startsWith('name')) {
                const currentLetter = this.documents[index].name.charAt(0).toUpperCase()
                const previousLetter = this.documents[index - 1].name.charAt(0).toUpperCase()

                return currentLetter !== previousLetter
            }

            return false
        },

        getSeparatorLabel (index) {
            if (!this.showSeparator(index)) return

            if (this.sortBy.startsWith('date')) {
                return this.$date(new Date(this.documents[index].date), 'y')
            }

            if (this.sortBy.startsWith('name')) {
                return this.documents[index].name.charAt(0).toUpperCase()
            }

            return ''
        },

        onFilterSubmit (filters) {
            this.showFilterModal = false

            if (!equals(this.$route.query, filters)) {
                this.search = filters.searchTerm
                this.filters = filters
                this.$router.push({ query: filters })

                this.loadDocuments()
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.sort {
    margin: auto 0 auto auto;

    @include media-breakpoint-up(md) {
        text-align: right;

        .row {
            justify-content: flex-end;
        }
    }
}
</style>
